import React, { useEffect, useRef } from 'react';
import { Mesh, Program, Renderer, Triangle } from 'ogl';

interface LiquidChromeProps extends React.HTMLAttributes<HTMLDivElement> {
  baseColor?: [number, number, number];
  speed?: number;
  amplitude?: number;
  frequencyX?: number;
  frequencyY?: number;
  interactive?: boolean;
}

export const LiquidChrome: React.FC<LiquidChromeProps> = ({
  baseColor = [0.1, 0.1, 0.1],
  speed = 0.2,
  amplitude = 0.5,
  frequencyX = 3,
  frequencyY = 2,
  interactive = true,
  ...props
}) => {
  const containerRef = useRef<HTMLDivElement | null>(null);

  const latestRef = useRef({
    baseColor,
    speed,
    amplitude,
    frequencyX,
    frequencyY,
    interactive,
  });

  useEffect(() => {
    latestRef.current = {
      baseColor,
      speed,
      amplitude,
      frequencyX,
      frequencyY,
      interactive,
    };
  }, [baseColor, speed, amplitude, frequencyX, frequencyY, interactive]);

  useEffect(() => {
    const containerEl = containerRef.current;
    if (!containerEl) return;
    const container: HTMLDivElement = containerEl;

    const renderer = new Renderer({ antialias: true, alpha: true, premultipliedAlpha: false });
    const gl = renderer.gl;
    gl.clearColor(0, 0, 0, 0);
    container.appendChild(gl.canvas);

    const vertexShader = `
      attribute vec2 position;
      attribute vec2 uv;
      varying vec2 vUv;
      void main() {
        vUv = uv;
        gl_Position = vec4(position, 0.0, 1.0);
      }
    `;

    const fragmentShader = `
      precision highp float;
      uniform float uTime;
      uniform vec3 uResolution;
      uniform vec3 uBaseColor;
      uniform float uAmplitude;
      uniform float uFrequencyX;
      uniform float uFrequencyY;
      uniform vec2 uMouse;
      varying vec2 vUv;

      vec4 renderImage(vec2 uvCoord) {
        vec2 fragCoord = uvCoord * uResolution.xy;
        vec2 uv = (2.0 * fragCoord - uResolution.xy) / min(uResolution.x, uResolution.y);

        for (float i = 1.0; i < 10.0; i++){
          uv.x += uAmplitude / i * cos(i * uFrequencyX * uv.y + uTime + uMouse.x * 3.14159);
          uv.y += uAmplitude / i * cos(i * uFrequencyY * uv.x + uTime + uMouse.y * 3.14159);
        }

        vec2 diff = (uvCoord - uMouse);
        float dist = length(diff);
        float falloff = exp(-dist * 20.0);
        float ripple = sin(10.0 * dist - uTime * 2.0) * 0.03;
        uv += (diff / (dist + 0.0001)) * ripple * falloff;

        // Soft circular mask (for smoother edges inside rounded container)
        float r = distance(uvCoord, vec2(0.5));
        float mask = smoothstep(0.5, 0.47, r);

        // Orange + white palette, with smooth "chrome" highlights (avoid division blowouts)
        vec3 orange = uBaseColor;
        vec3 white = vec3(1.0);

        float t = uTime * 1.2;
        float bands = 0.5 + 0.5 * sin(t + uv.x * 2.2 - uv.y * 1.8);
        float sheen = pow(bands, 3.0);
        float micro = 0.5 + 0.5 * sin(t * 1.7 + uv.x * 6.0 + uv.y * 5.0);
        sheen += micro * 0.15;
        sheen = clamp(sheen, 0.0, 1.0);

        vec3 base = mix(white, orange, 0.75);
        vec3 color = mix(base, white, sheen);

        // Gentle vignette for depth
        float vignette = smoothstep(0.95, 0.15, length(uvCoord - 0.5));
        color *= 0.85 + 0.25 * vignette;

        return vec4(color * mask, mask);
      }

      void main() {
        vec4 col = vec4(0.0);
        int samples = 0;
        for (int i = -1; i <= 1; i++){
          for (int j = -1; j <= 1; j++){
            vec2 offset = vec2(float(i), float(j)) * (1.0 / min(uResolution.x, uResolution.y));
            col += renderImage(vUv + offset);
            samples++;
          }
        }
        gl_FragColor = col / float(samples);
      }
    `;

    const geometry = new Triangle(gl);

    const uResolution = new Float32Array([1, 1, 1]);
    const uBaseColor = new Float32Array(latestRef.current.baseColor);
    const uMouse = new Float32Array([0.5, 0.5]);

    const program = new Program(gl, {
      vertex: vertexShader,
      fragment: fragmentShader,
      uniforms: {
        uTime: { value: 0 },
        uResolution: { value: uResolution },
        uBaseColor: { value: uBaseColor },
        uAmplitude: { value: 0.5 },
        uFrequencyX: { value: 3 },
        uFrequencyY: { value: 2 },
        uMouse: { value: uMouse },
      },
    });

    const mesh = new Mesh(gl, { geometry, program });

    function resize() {
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      const width = Math.max(1, container.clientWidth);
      const height = Math.max(1, container.clientHeight);
      renderer.setSize(width * dpr, height * dpr);
      gl.canvas.style.width = `${width}px`;
      gl.canvas.style.height = `${height}px`;
      uResolution[0] = gl.canvas.width;
      uResolution[1] = gl.canvas.height;
      uResolution[2] = gl.canvas.width / gl.canvas.height;
    }

    window.addEventListener('resize', resize);
    resize();

    function setMouseFromClient(clientX: number, clientY: number) {
      const rect = container.getBoundingClientRect();
      const x = (clientX - rect.left) / rect.width;
      const y = 1 - (clientY - rect.top) / rect.height;
      uMouse[0] = Math.max(0, Math.min(1, x));
      uMouse[1] = Math.max(0, Math.min(1, y));
    }

    const handleMouseMove = (event: MouseEvent) => setMouseFromClient(event.clientX, event.clientY);
    const handleTouchMove = (event: TouchEvent) => {
      if (event.touches.length > 0) setMouseFromClient(event.touches[0].clientX, event.touches[0].clientY);
    };

    if (latestRef.current.interactive) {
      container.addEventListener('mousemove', handleMouseMove);
      container.addEventListener('touchmove', handleTouchMove, { passive: true });
    }

    let animationId: number;
    let smoothedAmp = 0;
    let lastT = 0;
    const update = (t: number) => {
      animationId = requestAnimationFrame(update);
      const latest = latestRef.current;

      const dt = Math.min(0.05, Math.max(0, (t - lastT) * 0.001));
      lastT = t;

      program.uniforms.uTime.value = t * 0.001 * (latest.speed ?? 0.2);

      const amp = Math.max(0, Math.min(1, latest.amplitude ?? 0));
      // Smooth amplitude to avoid jitter/flicker on rapid updates.
      const k = 1.0 - Math.pow(0.001, dt); // framerate-independent smoothing
      smoothedAmp += (amp - smoothedAmp) * Math.min(0.25, k);

      // Keep it visible at low levels; clamp to avoid blown-out values.
      program.uniforms.uAmplitude.value = Math.max(0, Math.min(1.1, 0.12 + smoothedAmp * 0.85));

      program.uniforms.uFrequencyX.value = latest.frequencyX ?? 3;
      program.uniforms.uFrequencyY.value = latest.frequencyY ?? 2;

      const c = latest.baseColor ?? [0.1, 0.1, 0.1];
      uBaseColor[0] = c[0];
      uBaseColor[1] = c[1];
      uBaseColor[2] = c[2];

      renderer.render({ scene: mesh });
    };

    animationId = requestAnimationFrame(update);

    return () => {
      cancelAnimationFrame(animationId);
      window.removeEventListener('resize', resize);
      container.removeEventListener('mousemove', handleMouseMove);
      container.removeEventListener('touchmove', handleTouchMove);
      if (gl.canvas.parentElement) gl.canvas.parentElement.removeChild(gl.canvas);
      gl.getExtension('WEBGL_lose_context')?.loseContext();
    };
  }, []);

  return <div ref={containerRef} className="w-full h-full" {...props} />;
};

export default LiquidChrome;

